import React from 'react';
import { t } from 'i18next';
import moment from 'moment';
import DataTable from '../util/DataTable';

interface User {
  id: number;
  first_name: string;
  last_name: string;
}

interface LogEntry {
  id: number;
  service: string;
  status: string;
  description: string | null;
  createdAt: string;
  userId: number;
  user?: User | null;
}

interface LogsProps {
  history: LogEntry[];
  users: User[];
}

const SERVICE_OPTIONS = [
  { value: 'mail', label: 'mail' },
  { value: 'expenses', label: 'expenses' },
  { value: 'car_expenses', label: 'car_expenses' },
  { value: 'finance_income', label: 'finance_income' },
  { value: 'equipment', label: 'equipment' },
  { value: 'booking', label: 'booking' },
  { value: 'disk', label: 'disk' },
  { value: 'document', label: 'document' },
  { value: 'department', label: 'department' },
  { value: 'user', label: 'user' },
  { value: 'training_course', label: 'training_course' },
  { value: 'crm_contact', label: 'crm_contact' },
  { value: 'crm_organisation', label: 'crm_organisation' },
  { value: 'crm_project', label: 'crm_project' },
];

const Logs: React.FC<LogsProps> = ({ history, users }) => {
  const columns = [
    {
      key: 'createdAt',
      label: t('Date'),
      sortable: true,
      filterType: 'daterange' as const,
      sortExtract: (row: LogEntry) => new Date(row.createdAt).getTime(),
      filterExtract: (row: LogEntry) => row.createdAt ?? '',
      render: (row: LogEntry) => (
        <span className="text-[12px] text-[var(--text-color-calendar)]">
          {moment(row.createdAt).format('DD.MM.YYYY HH:mm')}
        </span>
      ),
      width: 140,
    },
    {
      key: 'service',
      label: t('LogService'),
      filterType: 'select' as const,
      filterOptions: SERVICE_OPTIONS,
      filterExtract: (row: LogEntry) => row.service ?? '',
      render: (row: LogEntry) => (
        <span className="text-[12px] font-medium text-[var(--text-color)]">
          {row.service}
        </span>
      ),
      width: 140,
    },
    {
      key: 'status',
      label: t('Status'),
      filterType: 'select' as const,
      filterOptions: [
        { value: 'success', label: t('LogSuccess') },
        { value: 'error', label: t('LogError') },
      ],
      filterExtract: (row: LogEntry) => row.status ?? '',
      render: (row: LogEntry) => {
        const ok = row.status === 'success';
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: ok ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)',
              color: ok ? '#16a34a' : '#dc2626',
            }}
          >
            {ok ? t('LogSuccess') : t('LogError')}
          </span>
        );
      },
      width: 90,
      align: 'center' as const,
    },
    {
      key: 'user',
      label: t('User'),
      filterType: 'text' as const,
      filterExtract: (row: LogEntry) =>
        row.user
          ? `${row.user.first_name} ${row.user.last_name}`
          : String(row.userId),
      render: (row: LogEntry) =>
        row.user ? (
          <span className="text-[13px] text-[var(--text-color)]">
            {row.user.first_name} {row.user.last_name}
          </span>
        ) : (
          <span className="text-[12px] text-[var(--text-color-calendar-week)]">
            #{row.userId}
          </span>
        ),
      width: 160,
    },
    {
      key: 'description',
      label: t('Description'),
      filterType: 'text' as const,
      filterExtract: (row: LogEntry) => row.description ?? '',
      defaultVisible: false,
      render: (row: LogEntry) => (
        <span className="text-[12px] text-[var(--text-color-calendar-week)] break-all">
          {row.description}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      rows={history ?? []}
      getKey={(row) => row.id}
      columns={columns}
      csvFilename="action-logs.csv"
      storageKey="history-logs-table"
      emptyText={t('Empty')}
    />
  );
};

export default Logs;
